/**
 * Browser-side CPARS/QASP extraction for Assist (SAD-904).
 * Same intelligence create/start/poll path as IntelligenceReextractButton /
 * ScoreDocumentUpload's Extract control; maps destination fields onto upsert params.
 */ import { IntelligenceTypes } from '@mono/proto-clients';
import { intelligenceClient } from '../../../lib/connect/clients';
const POLL_MS = 2000;
const MAX_WAIT_MS = 120000;
const CPARS_DEST_TO_PARAM = {
    [IntelligenceTypes.DestinationFieldName.PERIOD_START]: 'eval_period_start',
    [IntelligenceTypes.DestinationFieldName.PERIOD_END]: 'eval_period_end',
    [IntelligenceTypes.DestinationFieldName.OVERALL_SCORE]: 'overall_score',
    [IntelligenceTypes.DestinationFieldName.QUALITY_SCORE]: 'quality_score',
    [IntelligenceTypes.DestinationFieldName.SCHEDULE_SCORE]: 'schedule_score',
    [IntelligenceTypes.DestinationFieldName.COST_CONTROL_SCORE]: 'cost_control_score',
    [IntelligenceTypes.DestinationFieldName.MANAGEMENT_SCORE]: 'management_score',
    [IntelligenceTypes.DestinationFieldName.SMALL_BUSINESS_SCORE]: 'small_business_score',
    [IntelligenceTypes.DestinationFieldName.REGULATORY_COMPLIANCE_SCORE]: 'regulatory_compliance_score',
    [IntelligenceTypes.DestinationFieldName.OVERALL_COMMENT]: 'overall_comment',
    [IntelligenceTypes.DestinationFieldName.CONCURRENCE_STATUS]: 'concurrence_status',
    [IntelligenceTypes.DestinationFieldName.QUALITY_NARRATIVE]: 'quality_narrative',
    [IntelligenceTypes.DestinationFieldName.SCHEDULE_NARRATIVE]: 'schedule_narrative',
    [IntelligenceTypes.DestinationFieldName.COST_CONTROL_NARRATIVE]: 'cost_control_narrative',
    [IntelligenceTypes.DestinationFieldName.MANAGEMENT_NARRATIVE]: 'management_narrative',
    [IntelligenceTypes.DestinationFieldName.SMALL_BUSINESS_NARRATIVE]: 'small_business_narrative',
    [IntelligenceTypes.DestinationFieldName.REGULATORY_COMPLIANCE_NARRATIVE]: 'regulatory_compliance_narrative'
};
const QASP_DEST_TO_PARAM = {
    [IntelligenceTypes.DestinationFieldName.QUARTER]: 'quarter',
    [IntelligenceTypes.DestinationFieldName.PERIOD_START]: 'report_period_start',
    [IntelligenceTypes.DestinationFieldName.PERIOD_END]: 'report_period_end',
    [IntelligenceTypes.DestinationFieldName.OVERALL_SCORE]: 'overall_score',
    [IntelligenceTypes.DestinationFieldName.TECHNICAL_SCORE]: 'technical_score',
    [IntelligenceTypes.DestinationFieldName.SCHEDULE_SCORE]: 'schedule_score',
    [IntelligenceTypes.DestinationFieldName.COST_STAFFING_SCORE]: 'cost_staffing_score',
    [IntelligenceTypes.DestinationFieldName.MANAGEMENT_SCORE]: 'management_score',
    [IntelligenceTypes.DestinationFieldName.TECHNICAL_NARRATIVE]: 'technical_narrative',
    [IntelligenceTypes.DestinationFieldName.SCHEDULE_NARRATIVE]: 'schedule_narrative',
    [IntelligenceTypes.DestinationFieldName.COST_STAFFING_NARRATIVE]: 'cost_staffing_narrative',
    [IntelligenceTypes.DestinationFieldName.MANAGEMENT_NARRATIVE]: 'management_narrative'
};
function proposalScalar(v) {
    if (v == null) return undefined;
    if (typeof v === 'string' || typeof v === 'number') return v;
    if (typeof v === 'boolean') return String(v);
    if (typeof v === 'object' && v !== null && 'kind' in v) {
        const kind = v.kind;
        const caseName = kind?.case;
        const value = kind?.value;
        if (caseName === 'stringValue' && typeof value === 'string') return value;
        if (caseName === 'numberValue' && typeof value === 'number') return value;
        if (caseName === 'boolValue') return String(value);
    }
    return String(v);
}
async function sleep(ms) {
    await new Promise((r)=>setTimeout(r, ms));
}
/** Plain-language fallback when errorMessage is empty — mirrors IntelligenceFailureNotice. */ const FAILURE_REASON_MESSAGE = {
    [IntelligenceTypes.IntelligenceRunFailureReason.UNSUPPORTED_EVIDENCE]: 'This document could not be processed.',
    [IntelligenceTypes.IntelligenceRunFailureReason.PROVIDER_INCOMPLETE_RESPONSE]: 'The model response was incomplete, so the extraction could not finish.',
    [IntelligenceTypes.IntelligenceRunFailureReason.PROVIDER_UNAVAILABLE]: 'The extraction service was unavailable.',
    [IntelligenceTypes.IntelligenceRunFailureReason.NO_RESULTS]: 'The extraction completed but found no usable results.',
    [IntelligenceTypes.IntelligenceRunFailureReason.INTERNAL]: 'The extraction failed unexpectedly.'
};
function intelligenceRunFailureMessage(run) {
    const fromServer = run.errorMessage?.trim();
    if (fromServer) return fromServer;
    if (run.failureReason != null) {
        const mapped = FAILURE_REASON_MESSAGE[run.failureReason];
        if (mapped) return mapped;
    }
    return 'Extraction failed';
}
/** Start extraction and wait until READY_FOR_REVIEW / PARTIALLY_READY (or fail). */ export async function extractQualityScoreParams(input) {
    const workflowKind = input.kind === 'cpars' ? IntelligenceTypes.WorkflowKind.CPARS : IntelligenceTypes.WorkflowKind.QASP;
    const entityType = input.kind === 'cpars' ? 'CPARSScore' : 'QASPScore';
    const created = await intelligenceClient.createIntelligenceRun({
        workflowKind,
        target: {
            programId: input.programId,
            entityType,
            entityId: ''
        },
        sourceArtifactIds: [
            input.artifactId
        ]
    });
    const runId = created.run?.id ?? '';
    if (!runId) throw new Error('Intelligence run was not created.');
    await intelligenceClient.startIntelligenceRun({
        runId
    });
    const started = Date.now();
    while(Date.now() - started < MAX_WAIT_MS){
        const resp = await intelligenceClient.getIntelligenceRun({
            runId
        });
        const run = resp.run;
        if (!run) throw new Error('Intelligence run disappeared.');
        if (run.state === IntelligenceTypes.IntelligenceRunState.FAILED || run.state === IntelligenceTypes.IntelligenceRunState.CANCELLED) {
            throw new Error(intelligenceRunFailureMessage(run));
        }
        if (run.state === IntelligenceTypes.IntelligenceRunState.READY_FOR_REVIEW || run.state === IntelligenceTypes.IntelligenceRunState.PARTIALLY_READY) {
            const map = input.kind === 'cpars' ? CPARS_DEST_TO_PARAM : QASP_DEST_TO_PARAM;
            const out = {};
            for (const p of run.proposals ?? []){
                const dest = p.destination?.fieldName ?? IntelligenceTypes.DestinationFieldName.UNSPECIFIED;
                const param = map[dest];
                if (!param) continue;
                const raw = p.reviewedValue ?? p.proposedValue;
                const scalar = proposalScalar(raw);
                if (scalar !== undefined && scalar !== '') {
                    out[param] = scalar;
                }
            }
            return out;
        }
        await sleep(POLL_MS);
    }
    throw new Error('Extraction timed out — open Quality and extract there, then retry.');
}
